import React, { useState, useEffect, useCallback, useContext } from "react";
import searchApiService from "../service/searchApiService";
import { useConfig } from "./useConfig";
import {
  dispatchGA4SearchEvent,
  dispatchSetupAnchorsEvent,
} from "../utils/events";

const FILTER_PREFIX = "f_";
const PAGE_PREFIX = "page";

const FiltersContext = React.createContext();

export const FiltersProvider = ({ children }) => {
  const [data, setData] = useState();
  const [page, setPage] = useState(1);
  const [error, setError] = useState();
  const [isLoading, setLoading] = useState(false);
  const [initialFilters, setInitialFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [order, setOrder] = useState("");
  const [search, setSearch] = useState({ input: "" });
  const [anchor, setAnchor] = useState("");
  const [cacheKey, setCacheKey] = useState("");
  const [anchorsApplied, setAnchorsApplied] = useState([]);
  const [useAnchors, setUseAnchors] = useState(false);

  const { customParams } = useConfig();

  const getFiltersQuery = () => {
    if (selectedFilters?.length > 0) {
      return selectedFilters.map(({ name, values }) => ({
        facet: name,
        values,
      }));
    }

    return getUrlFilters();
  };

  const getUrlFilters = (data) => {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = [];
    const newSelectedFilters = [];

    for (const [key, value] of params.entries()) {
      if (key.startsWith(FILTER_PREFIX)) {
        const filterName = key.replace(FILTER_PREFIX, "");

        const processValue = (v) => {
          const number = Number(v);
          return !isNaN(number) && v !== "" ? number : v;
        };

        let filterValues;
        if (value.includes(",")) {
          filterValues = value.split(",").map(processValue);
        } else {
          const processedValue = processValue(value);
          filterValues = [processedValue];
        }

        urlFilters.push({
          facet: filterName,
          values: filterValues,
        });

        newSelectedFilters.push({
          name: filterName,
          values: filterValues,
        });
      }
    }

    if (data?.meta?.results_count === 0) {
      return;
    }

    setSelectedFilters(newSelectedFilters);
    return urlFilters;
  };

  const updateFiltersURL = () => {
    const url = new URL(window.location);
    const keysToRemove = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith(FILTER_PREFIX)) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => url.searchParams.delete(key));

    selectedFilters.forEach(({ name, values }) => {
      const key = `${FILTER_PREFIX}${name}`;
      if (Array.isArray(values)) {
        url.searchParams.set(key, values.join(","));
      } else {
        url.searchParams.set(key, values.toString());
      }
    });

    window.history.replaceState({}, "", url.toString());
  };

  const updatePageQuery = (page) => {
    const url = new URL(window.location);
    const keysToRemove = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith(PAGE_PREFIX)) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => url.searchParams.delete(key));

    if (page) {
      url.searchParams.set(PAGE_PREFIX, page);
    } else {
      url.searchParams.set(PAGE_PREFIX, 1);
    }

    window.history.replaceState({}, "", url.toString());
  };

  const clearFilters = (customParamsTriggered = false) => {
    updateFiltersURL();
    setData(null);
    setPage(1);
    if (!customParamsTriggered) setSelectedFilters([]);
    setCacheKey(null);
  };

  const handleError = (error) => {
    console.error(error);
    setError(true);
    setData(null);
    setLoading(false);

    dispatchGA4SearchEvent({
      eventAction: "api_error",
      eventLabel: search?.input,
    });
  };

  const runQuery = useCallback(
    async (uuid, word, anchor) => {
      if (anchor && !word) word = " ";
      if (!uuid || !word) return;

      let userCustomParams = "";
      Object.entries(customParams)?.map(
        ([paramKey, paramValue]) =>
          (userCustomParams = userCustomParams + `&${paramKey}=${paramValue}`)
      );

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const pageQuery = urlParams.get(PAGE_PREFIX) || 1;

      const handleSuccess = (res) => {
        setError(false);

        const data = res.data;
        setData(data);
        setInitialFilters(data.meta?.filters_applied || getUrlFilters(data));
        setPage(res.data.meta.pages_current);
        setCacheKey(res.data.cacheKey || "");
        setLoading(false);

        if (!data?.meta?.results_count) {
          dispatchGA4SearchEvent({
            eventAction: "no_results",
            eventLabel:
              data?.meta?.term ||
              data?.meta?.anchors_applied
                ?.map(
                  ({ name, tree_key }) => `${name} (${tree_key?.split(":")[0]})`
                )
                ?.join(", "),
          });
        }

        return data;
      };

      const fetchApi = async () =>
        await searchApiService
          .getMetaResults(uuid, word, anchor, userCustomParams, pageQuery)
          .then(handleSuccess)
          .catch(handleError);

      setLoading(true);
      return await fetchApi();
    },
    [customParams]
  );

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  useEffect(() => {
    if (data?.meta?.anchors_applied?.length > 0) {
      setUseAnchors(true);
      setAnchorsApplied(data.meta.anchors_applied);

      data.meta.anchors_applied[0].checked = true;

      const anchors_applied = [...data.meta.anchors_applied];

      dispatchSetupAnchorsEvent({ anchors_applied });
    } else if (data?.meta?.anchors_applied) {
      setUseAnchors(false);
      setAnchorsApplied([]);
    }
  }, [data]);

  return (
    <FiltersContext.Provider
      value={{
        data,
        isLoading,
        error,
        refetch: runQuery,
        selectedFilters,
        changeFilter: setSelectedFilters,
        order,
        changeOrder: setOrder,
        page,
        changePage: setPage,
        getFiltersQuery,
        search,
        setSearch,
        clearFilters,
        setData,
        initialFilters,
        cacheKey,
        setCacheKey,
        anchor,
        setAnchor,
        setLoading,
        anchorsApplied,
        setAnchorsApplied,
        useAnchors,
        setUseAnchors,
        updateFiltersURL,
        updatePageQuery,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = () => {
  const {
    data,
    isLoading,
    error,
    refetch,
    selectedFilters,
    changeFilter,
    order,
    changeOrder,
    page,
    changePage,
    getFiltersQuery,
    search,
    setSearch,
    searchKeyNumber,
    setSearchKeyNumber,
    clearFilters,
    setData,
    initialFilters,
    cacheKey,
    setCacheKey,
    anchor,
    setAnchor,
    setLoading,
    anchorsApplied,
    setAnchorsApplied,
    useAnchors,
    setUseAnchors,
    updateFiltersURL,
    updatePageQuery,
  } = useContext(FiltersContext);
  return {
    data,
    isLoading,
    error,
    refetch,
    selectedFilters,
    changeFilter,
    order,
    changeOrder,
    page,
    changePage,
    getFiltersQuery,
    search,
    setSearch,
    searchKeyNumber,
    setSearchKeyNumber,
    clearFilters,
    setData,
    initialFilters,
    cacheKey,
    setCacheKey,
    anchor,
    setAnchor,
    setLoading,
    anchorsApplied,
    setAnchorsApplied,
    useAnchors,
    setUseAnchors,
    updateFiltersURL,
    updatePageQuery,
  };
};
